import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { setResponseHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

export interface ScopeDocument {
  client: string;
  title: string;
  version: string;
  date: string;
  sections: { heading: string; items: { text: string; heading: boolean }[] }[];
  deliverables: { name: string; includes: string; fee: number; status: string }[];
  total: number;
  currency: string;
}
export interface ScopeReview {
  document: ScopeDocument;
  documentHash: string;
  role: "client" | "consultant";
  signatures: {
    role: "client" | "consultant";
    name: string | null;
    signedAt: string | null;
    documentHash: string | null;
  }[];
}

const tokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

async function hashToken(token: string) {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function scopeClient(url: string, key: string) {
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`)
          headers.delete("Authorization");
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export const reviewScope = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string }) => z.object({ token: tokenSchema }).parse(input))
  .handler(async ({ data }): Promise<ScopeReview | null> => {
    setResponseHeader("Cache-Control", "no-store");
    const url = process.env["SUPABASE_URL"];
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
    if (!url || !key) throw new Error("Document service unavailable.");
    const { data: result, error } = await scopeClient(url, key).rpc("review_client_scope", {
      p_token_hash: await hashToken(data.token),
    });
    if (error) throw new Error("Unable to load this document. Please try again.");
    return result as unknown as ScopeReview | null;
  });

export const signScope = createServerFn({ method: "POST" })
  .inputValidator(
    (input: {
      token: string;
      documentHash: string;
      name: string;
      email: string;
      consent: boolean;
    }) =>
      z
        .object({
          token: tokenSchema,
          documentHash: z.string().regex(/^[a-f0-9]{64}$/),
          name: z.string().trim().min(2).max(120),
          email: z.string().trim().email().max(254),
          consent: z.literal(true),
        })
        .parse(input),
  )
  .handler(async ({ data }) => {
    setResponseHeader("Cache-Control", "no-store");
    const url = process.env["SUPABASE_URL"];
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
    if (!url || !key) throw new Error("Document service unavailable.");
    const { data: saved, error } = await scopeClient(url, key).rpc("sign_client_scope", {
      p_token_hash: await hashToken(data.token),
      p_document_hash: data.documentHash,
      p_name: data.name,
      p_email: data.email,
    });
    if (error || !saved)
      throw new Error("Your signature could not be saved. Please refresh and try again.");
    return { saved: true };
  });
